import React, { useMemo, useState } from "react";

import styled from "styled-components";
import { ISimpleImage } from "@services/ProductImages";
import { FaChevronLeft, FaChevronRight, FaExpand, FaPlayCircle, FaTimes } from "react-icons/fa";
import { Dialog } from "@components/Catalyst/dialog";
import { Navbar, NavbarItem } from "@components/Catalyst/navbar";
import { Button } from "@components/Catalyst/button";

const Iframe = styled.iframe`
  width: 100%;
  height: 325px;
  border: 0px;
`;

interface ISafeSimpleImage {
  fullImageUrl: string;
  altText: string;
}

interface ISafeVideo {
  video_embed_id: string;
}

interface ISafeDisplayable {
  key: string;

  image?: ISafeSimpleImage;
  video?: ISafeVideo;
  caption: string | undefined | null;
}

interface IProps {
  files: ISimpleImage[];
}

const MultiMediaViewer = ({ files }: IProps) => {
  const [fullScreenImage, setFullScreenImage] = useState<null | ISafeSimpleImage>(null);
  const [currImageIndex, setCurrImageIndex] = useState(0);

  // check images and weed out any nulls or undefined
  const safeImages = useMemo(
    () =>
      files.reduce<ISafeDisplayable[]>((result, value) => {
        if (value.vimeo_embed_id) {
          result.push({ key: value.vimeo_embed_id, video: { video_embed_id: value.vimeo_embed_id }, caption: value.caption });
        } else if (value.fullImageUrl && value.altText) {
          result.push({ key: value.id, image: { fullImageUrl: value.fullImageUrl, altText: value.altText }, caption: value.caption });
        }

        return result;
      }, []),
    [],
  );

  const bigImage = safeImages[currImageIndex];

  return (
    <>
      <div className="flex w-full flex-col gap-2">
        <Navbar>
          <NavbarItem onClick={() => setCurrImageIndex(currImageIndex - 1 < 0 ? safeImages.length - 1 : currImageIndex - 1)}>
            <FaChevronLeft className="h-3 w-3 md:h-5 md:w-5" />
          </NavbarItem>

          <div className="flex flex-wrap place-content-evenly gap-y-2">
            {safeImages.map((i, index) => (
              <NavbarItem
                key={index}
                onClick={() => {
                  setCurrImageIndex(index);
                }}
                current={currImageIndex === index}
              >
                {i.image && <img className="h-14 w-14" src={i.image.fullImageUrl} alt={i.image.altText} />}
                {i.video && (
                  <div className="grid h-14 w-14 place-items-center bg-gray-400">
                    <FaPlayCircle className="h-10 w-10 text-white" />
                  </div>
                )}
              </NavbarItem>
            ))}
          </div>
          <NavbarItem onClick={() => setCurrImageIndex((currImageIndex + 1) % safeImages.length)}>
            <FaChevronRight className="h-3 w-3 md:h-5 md:w-5" />
          </NavbarItem>
        </Navbar>

        <div className="flex flex-col place-items-center">
          {bigImage?.image && (
            <div className="relative w-fit">
              <img src={bigImage.image.fullImageUrl} alt={bigImage.image.altText} />
              <div className="invisible absolute right-0 top-0 md:visible">
                <Button onClick={() => setFullScreenImage(bigImage?.image ?? null)}>
                  <FaExpand className="h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
          {bigImage?.video && (
            <div className="h-96 w-full max-w-full">
              {bigImage.video.video_embed_id.startsWith("https://images.") ? (
                <Iframe
                  src={bigImage.video.video_embed_id}
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  style={{ top: 0, left: 0, padding: 0, width: "100%", height: "100%" }}
                  allowFullScreen
                />
              ) : (
                <Iframe
                  src={`https://player.vimeo.com/video/${bigImage.video.video_embed_id}/?h=605397c805&portrait=0&byline=0&title=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          )}

          {bigImage?.caption && <div>{bigImage.caption}</div>}
        </div>
      </div>
      <Dialog size="5xl" onClose={() => setFullScreenImage(null)} open={!!fullScreenImage}>
        <div className="flex place-content-end">
          <Button onClick={() => setFullScreenImage(null)}>
            <FaTimes className="h-5 w-5" />
          </Button>
        </div>
        {fullScreenImage && (
          <div className="w-full overflow-scroll">
            <img className="max-h-none max-w-none" src={fullScreenImage.fullImageUrl} alt={fullScreenImage.altText} />
          </div>
        )}
      </Dialog>
    </>
  );
};
export default MultiMediaViewer;
